import React, { createContext, Dispatch, MutableRefObject, useMemo } from 'react';
import { MandatoryChildren } from '../../../models/children-props';
import { postEditorReducer } from './PostEditorReducer';
import { InitialPostEditorState } from './initial';
import { PostEditorPublicAction } from './PostEditorActions';
import { PostEditorState } from '../../../models/post-editor';
import { useEffectReducer } from 'use-effect-reducer';
import { postEditorEffects } from './PostEditorEffects';
import { MappedCategoriesType } from '../../Publish/Categories/Categories.helpers';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { useDidMount } from '../../../hooks/useDidMount';
import { CustomEditor } from '../../../models/slate-editor';

export type EditorRefType = {
  id: number;
  editor: CustomEditor;
};

export type EditorUpdaterPayload = { type: 'add'; value: EditorRefType } | { type: 'remove'; value: number };
type EditorUpdaterFunction = (
  payload: EditorUpdaterPayload
) => (ref: PostEditorsDataType[0], postGroupIndex: number) => void;
export type PostEditorsDataType = [MutableRefObject<EditorRefType[][]>, EditorUpdaterFunction];

export const EditorsInitial: MutableRefObject<EditorRefType[][]> = { current: [[]] };

const editorsUpdater: EditorUpdaterFunction =
  (payload): ((ref: MutableRefObject<EditorRefType[][]>, postGroupIndex: number) => void) =>
  (ref, postGroupIndex): void => {
    const { type, value } = payload;
    switch (type) {
      case 'add': {
        ref.current[postGroupIndex] = [...ref.current[postGroupIndex], value];
        break;
      }
      case 'remove': {
        if (ref.current[postGroupIndex].length) {
          ref.current[postGroupIndex] = ref.current[postGroupIndex].filter(item => item.id !== value);
        }
        break;
      }
    }
  };

const InitialPostEditorsData: PostEditorsDataType = [EditorsInitial, editorsUpdater];

type PostEditorContextType = {
  state: PostEditorState;
  dispatch: Dispatch<PostEditorPublicAction>;
  profiles: SocialProfileWithBoardsAndMentionType[];
  categories: MappedCategoriesType[];
  loading: boolean;
  initialized: boolean;
  editors: PostEditorsDataType;
};

export const PostEditorContext = createContext<PostEditorContextType>({
  state: InitialPostEditorState(),
  dispatch: () => {
    //
  },
  profiles: [],
  categories: [],
  loading: true,
  initialized: false,
  editors: InitialPostEditorsData
});

export type PostEditorProviderProps = {
  initialState: PostEditorState;
  profiles: SocialProfileWithBoardsAndMentionType[];
  categories: MappedCategoriesType[];
  loading: boolean;
  payload?: PostEditorPublicAction | PostEditorPublicAction[];
};

export const PostEditorProvider = ({
  children,
  initialState,
  profiles,
  categories,
  loading,
  payload
}: MandatoryChildren<PostEditorProviderProps>): JSX.Element => {
  const editors: PostEditorsDataType = useMemo(() => {
    return [{ current: initialState.postGroups.map(_ => []) }, editorsUpdater];
  }, [initialState]);
  const [state, dispatch] = useEffectReducer(postEditorReducer, initialState, postEditorEffects);

  useDidMount(() => {
    if (payload) {
      const payloads = Array.isArray(payload) ? payload : [payload];
      for (const payload of payloads) {
        dispatch(payload);
      }
    }
  });

  const loadingValue = loading || state.loading;

  return (
    <PostEditorContext.Provider
      value={{
        state,
        dispatch,
        profiles,
        categories,
        loading: loadingValue,
        initialized: true,
        editors
      }}>
      {children}
    </PostEditorContext.Provider>
  );
};
